/**
 * Taxonomy schema — the contract every registry item satisfies.
 *
 * An item is plain data:
 *   {
 *     id, name,
 *     compatibleWith?:       { <fieldId>: [itemIds] }   — omit key = compatible with all
 *     requiresCapabilities?: ['supportsOffers', …]      — checked against the selected
 *                                                          document's Phase 2 knowledge
 *   }
 *
 * All filtering everywhere derives from these two properties — there are no
 * per-field if/else chains anywhere in the engine or the UI.
 */

import { CAPABILITIES } from '../documentKnowledge';

const CAPABILITY_KEYS = new Set(CAPABILITIES.map((c) => c.key));

/** Stable slug for generated ids (category ids derive from industry + name). */
export const slugify = (str) =>
  str.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/**
 * Validate one registry. Returns { valid, errors[] }.
 * `fieldIds` are the taxonomy field ids compatibleWith may reference.
 */
export const validateRegistry = (fieldId, items, fieldIds) => {
  const errors = [];
  if (!Array.isArray(items) || items.length === 0) {
    return { valid: false, errors: [`registry "${fieldId}" must be a non-empty array`] };
  }

  const seen = new Set();
  items.forEach((item) => {
    if (!item?.id || !item?.name) {
      errors.push(`${fieldId}: item ${JSON.stringify(item)} needs id and name`);
      return;
    }
    if (seen.has(item.id)) errors.push(`${fieldId}: duplicate id "${item.id}"`);
    seen.add(item.id);

    Object.keys(item.compatibleWith || {}).forEach((key) => {
      if (!fieldIds.includes(key)) {
        errors.push(`${fieldId}/${item.id}: compatibleWith references unknown field "${key}"`);
      }
    });
    (item.requiresCapabilities || []).forEach((cap) => {
      if (!CAPABILITY_KEYS.has(cap)) {
        errors.push(`${fieldId}/${item.id}: unknown capability "${cap}"`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
};
